<template>
  <GmapMap
    ref="gmap"
    :center="center"
    :zoom="mapZoom"
    :class="gmapClass"
    @tilesloaded="initDrawingManager"
    :options="mapOptions"
  />
</template>

<script>
export default {
  props: {
    zoom: {
      type: Number,
      default: 13,
    },
    defaultZones: {
      type: Array,
      default: () => [],
    },
    latitude: {
      type: Number,
      default: 19.4326077,
    },
    longitude: {
      type: Number,
      default: -99.133208,
    },
    width: {
      type: Number,
      default: 1000,
    },
    height: {
      type: Number,
      default: 500,
    },
    gmapClass: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      mapZoom: this.zoom,
      zones: [],
      isDrawing: false,
      drawingManager: null,
      center: { lat: this.latitude, lng: this.longitude },
      pendingDefaultZones: null,
      mapOptions: {
        streetViewControl: false,
        fullscreenControl: false,
        restriction: {
          latLngBounds: {
            north: 32.718,
            south: 14.532,
            west: -118.503,
            east: -86.589,
          },
          strictBounds: false,
        },
      },
    }
  },
  watch: {
    defaultZones: {
      immediate: true,
      handler(newZones) {
        if (newZones && newZones.length > 0) {
          if (this.mapReady) {
            this.loadDefaultZones(newZones)
          } else {
            this.pendingDefaultZones = newZones
          }
        }
      },
    },
    latitude: {
      immediate: true,
      handler(newLat) {
        this.center = this.getBoundedLatLng(newLat, this.longitude)
        this.marker = this.center
      },
    },
    longitude: {
      immediate: true,
      handler(newLng) {
        this.center = this.getBoundedLatLng(this.latitude, newLng)
        this.marker = this.center
      },
    },
  },
  methods: {
    initDrawingManager() {
      if (this.drawingManager) return

      const google = window.google
      const map = this.$refs.gmap.$mapObject

      this.drawingManager = new google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: false,
        drawingControlOptions: {
          position: google.maps.ControlPosition.TOP_CENTER,
          drawingModes: ["polygon"],
        },
        polygonOptions: {
          fillColor: "#2196F3",
          fillOpacity: 0.4,
          strokeColor: "#1976D2",
          strokeWeight: 2,
          editable: true,
          draggable: false,
        },
      })

      this.drawingManager.setMap(map)

      google.maps.event.addListener(
        this.drawingManager,
        "overlaycomplete",
        (event) => {
          if (event.type === "polygon") {
            const coordinates = []
            const path = event.overlay.getPath()
            for (let i = 0; i < path.getLength(); i++) {
              const point = path.getAt(i)
              coordinates.push({ lat: point.lat(), lng: point.lng() })
            }
            this.$emit("polygon-drawn", coordinates)
          }
        }
      )

      this.mapReady = true
      if (this.pendingDefaultZones) {
        this.loadDefaultZones(this.pendingDefaultZones)
        this.pendingDefaultZones = null
      }
    },
    getBoundedLatLng(lat, lng) {
      const bounds = {
        north: 32.718,
        south: 14.532,
        west: -118.503,
        east: -86.589,
      }

      return {
        lat: Math.max(bounds.south, Math.min(bounds.north, lat)),
        lng: Math.max(bounds.west, Math.min(bounds.east, lng)),
      }
    },
    loadDefaultZones(zones) {
      const google = window.google
      const map = this.$refs.gmap.$mapObject

      if (!map) {
        console.error("Map is not ready for loading default zones")
        this.pendingDefaultZones = zones
        return
      }

      zones.forEach((zone) => {
        if (this.zones.some((z) => z.name === zone.name)) return

        const color = this.getUniqueColor()
        const polygon = new google.maps.Polygon({
          paths: zone.coordinates,
          fillColor: color,
          fillOpacity: 0.4,
          strokeColor: color,
          strokeWeight: 2,
          editable: false,
          map: map,
        })

        const zoneId = Date.now().toString() + Math.random() // ID único
        polygon.set("zoneId", zoneId)

        const newZone = {
          id: zoneId,
          name: zone.name,
          coordinates: zone.coordinates,
          overlay: polygon,
          color,
        }

        this.zones.push(newZone)
        this.updateZoneLabel(zone)
      })

      this.$emit("zones", this.zones)
    },
    calculateCentroid(coordinates) {
      let x = 0,
        y = 0,
        n = coordinates.length
      coordinates.forEach((coord) => {
        x += coord.lat
        y += coord.lng
      })
      return { lat: x / n, lng: y / n }
    },
    updateZoneLabel(zone) {
      const google = window.google
      const map = this.$refs.gmap.$mapObject

      const centroid = this.calculateCentroid(zone.coordinates)

      zone.marker = new google.maps.Marker({
        position: centroid,
        map: map,
        label: {
          text: zone.name,
          color: "black",
          fontWeight: "bold",
          fontSize: "14px",
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 0,
        },
      })
    },
    getUniqueColor() {
      var letters = "0123456789ABCDEF"
      var color = "#"
      for (var i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)]
      }
      return color
    },
  },
}
</script>
